import { useCallback, useEffect, useState } from 'react';

/**
 * Small recently-visited cache for the Cmd-K command palette
 * (card spotlight-067).
 *
 * Lives in ``sessionStorage`` — palette recency dies with the tab so
 * a shared workstation doesn't leak the previous operator's nav
 * trail.  localStorage was tempting (recency carries across tabs)
 * but the recents are a UX nicety, not a setting; sessionStorage
 * keeps the cost of "wrong tenant context" close to zero.
 *
 * Shape per entry — kept identical to the search result row so the
 * palette renders recents and search hits with the same component:
 *
 *   {
 *     id: 'tour_xyz',
 *     kind: 'tour',
 *     label: 'Welcome tour',
 *     sub: 'Spotlight',
 *     url: '/projects/spotlight/tours/tour_xyz',
 *     project_slug: 'spotlight',  // optional
 *   }
 *
 * Capped at 10 — beyond that the palette's empty-state list gets
 * noisy and the operator can't visually parse it in 50ms.
 */

const STORAGE_KEY = 'spotlight.cmdk.recent';
const MAX_ITEMS = 10;

function readFromStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

function writeToStorage(items) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch {
    /* private mode / quota — silent failure keeps the palette usable */
  }
}

/**
 * React hook returning ``[items, pushRecent, clearRecent]``.
 *
 *   - ``items`` is always a defensively-copied array (never mutate
 *     the underlying storage directly).
 *   - ``pushRecent(item)`` prepends the item, de-duping by
 *     ``${kind}:${id}`` so re-clicking the same row promotes it to
 *     the top rather than stacking duplicates.
 *   - ``clearRecent()`` empties the list (currently unused, but the
 *     palette's "Clear recents" footer can wire to it later without
 *     a hook change).
 */
export function useRecentItems() {
  const [items, setItems] = useState(readFromStorage);

  // Cross-tab sync isn't a goal (sessionStorage is per-tab), but a
  // single tab opening two palette instances simultaneously would
  // otherwise see stale state.  ``storage`` doesn't fire for the
  // same tab that wrote — but we drive the in-memory cache from a
  // single source of truth, so this listener is a defence-in-depth
  // measure for split-screen window.opens that share the session.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function onStorage(e) {
      if (e.key !== STORAGE_KEY) return;
      setItems(readFromStorage());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const pushRecent = useCallback((item) => {
    if (!item || !item.id || !item.kind) return;
    setItems((prev) => {
      const key = `${item.kind}:${item.id}`;
      const filtered = prev.filter((p) => `${p.kind}:${p.id}` !== key);
      const next = [item, ...filtered].slice(0, MAX_ITEMS);
      writeToStorage(next);
      return next;
    });
  }, []);

  const clearRecent = useCallback(() => {
    writeToStorage([]);
    setItems([]);
  }, []);

  return [items, pushRecent, clearRecent];
}
